import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { discountAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const DiscountList = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadDiscounts();
  }, [typeFilter, statusFilter]);

  const loadDiscounts = async () => {
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.is_active = statusFilter;

      const response = await discountAPI.getAll(params);
      setDiscounts(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('discounts.deleteConfirm'))) return;
    try {
      await discountAPI.delete(id);
      loadDiscounts();
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const getTypeColor = (type) => {
    return type === 'percentage'
      ? { bg: 'bg-purple-500', text: 'text-purple-700' }
      : { bg: 'bg-green-500', text: 'text-green-700' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('discounts.discountTitle')}</h2>
          <p className="text-gray-600 mt-1">{t('discounts.manageOrderItems')}</p>
        </div>
        <Link
          to="/discounts/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('discounts.addDiscountButton')}
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6 flex gap-4">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('discounts.allTypes')}</option>
            <option value="percentage">{t('discounts.percentage')}</option>
            <option value="fixed">{t('discounts.fixedAmount')}</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('discounts.allStatus')}</option>
            <option value="1">{t('discounts.active')}</option>
            <option value="0">{t('discounts.inactive')}</option>
          </select>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-1">{t('discounts.status')}</div>
            <div className="col-span-3">{t('discounts.name')}</div>
            <div className="col-span-2">{t('discounts.type')}</div>
            <div className="col-span-2">{t('discounts.value')}</div>
            <div className="col-span-3">{t('discounts.description')}</div>
            <div className="col-span-1 text-right">{t('discounts.actions')}</div>
          </div>

          {/* Card Items */}
          {discounts.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {t('discounts.noDiscounts')}
            </div>
          ) : (
            discounts.map((discount) => {
              const typeColor = getTypeColor(discount.type);
              return (
                <div key={discount.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
                  <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                    {/* Status */}
                    <div className="col-span-1 flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${discount.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    </div>

                    {/* Name */}
                    <div className="col-span-3">
                      <div className="text-sm font-semibold text-gray-900">
                        {i18n.language === 'ar' && discount.name_ar ? discount.name_ar : discount.name}
                      </div>
                      {discount.name_ar && i18n.language !== 'ar' && (
                        <div className="text-xs text-gray-500 mt-0.5">{discount.name_ar}</div>
                      )}
                    </div>

                    {/* Type */}
                    <div className="col-span-2 flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${typeColor.bg}`}></div>
                      <span className={`text-sm font-medium capitalize ${typeColor.text}`}>
                        {discount.type}
                      </span>
                    </div>

                    {/* Value */}
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-orange-600">
                        {discount.type === 'percentage'
                          ? `${parseFloat(discount.value)}%`
                          : `${t('common.currency')} ${parseFloat(discount.value).toFixed(2)}`
                        }
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-span-3">
                      <div className="text-sm text-gray-600 truncate">
                        {discount.description || '-'}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex items-center justify-end gap-2">
                      <Link
                        to={`/discounts/${discount.id}`}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleDelete(discount.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountList;
